'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import { BannerSection } from '@/types/sanity';
import { CTAButton } from '@/components/cta/CTAButton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import Image from 'next/image';
import { urlFor } from '@/sanity/lib/image';
import { cn } from '@/lib/utils';

const bannerTitleComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => <span className="block">{children}</span>,
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
  },
};

const FALLBACK_BANNER_IMAGE = '/hom-banner-bg.png';

interface BannerSectionComponentProps {
  section: BannerSection;
  pageSlug?: string;
}

export function BannerSectionComponent({ section, pageSlug }: BannerSectionComponentProps) {
  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const slideCount = section.slides?.length || 0;
  const autoplay = section.autoplay !== false && slideCount > 1;
  const interval = section.autoplayInterval || 5000;

  useEffect(() => {
    if (!autoplay || isHovered) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slideCount);
    }, interval);
    return () => clearInterval(timer);
  }, [autoplay, isHovered, slideCount, interval]);
  const hasMultipleSlides = section.slides && section.slides.length > 1;
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  // Parallax transforms - faster, more impactful
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.3, 0.7], [1, 0.6, 0]);

  // Single slide with parallax
  if (!hasMultipleSlides && section.slides && section.slides.length > 0) {
    const slide = section.slides[0];
    const bgColor = slide.backgroundColor?.hex;
    const bgImageUrl = slide.backgroundImage ? urlFor(slide.backgroundImage).url() : undefined;
    const hasBackground = !!bgColor || !!bgImageUrl;

    return (
      <section
        ref={heroRef}
        className={cn(
          'banner-section relative overflow-hidden -mt-16 md:mt-0',
          pageSlug && `banner-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="relative aspect-9/16 md:aspect-video overflow-hidden">
            {bgColor ? (
              <div className="absolute inset-0" style={{ backgroundColor: bgColor }} />
            ) : hasBackground ? (
              <>
                {/* Mobile image */}
                <Image
                  src={urlFor(slide.mobileBackgroundImage || slide.backgroundImage!)
                    .width(1080)
                    .quality(85)
                    .url()}
                  alt=""
                  fill
                  priority
                  className="object-cover md:hidden"
                />
                {/* Desktop image */}
                <Image
                  src={urlFor(slide.backgroundImage!).width(1920).quality(85).url()}
                  alt=""
                  fill
                  priority
                  className="hidden md:block object-cover object-center"
                />
              </>
            ) : (
              <Image src={FALLBACK_BANNER_IMAGE} alt="" fill priority className="object-cover" />
            )}
            <motion.div
              style={{ y: contentY, opacity: contentOpacity }}
              className="relative z-20 h-full flex items-end md:items-center"
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="banner-slide text-left w-full md:max-w-2xl lg:max-w-[50%] bg-primary/90 backdrop-blur-md text-white px-6 py-6 md:bg-transparent md:backdrop-blur-none md:text-inherit md:py-0 md:px-16"
              >
                <div className="mb-4 md:mb-6">
                  <motion.h1
                    className="font-normal mb-4 text-banner-title leading-banner-title"
                    style={slide.titleColor?.hex ? { color: slide.titleColor.hex } : undefined}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                  >
                    <PortableText value={slide.title} components={bannerTitleComponents} />
                  </motion.h1>
                  {slide.subtitle && (
                    <motion.p
                      className={cn(
                        'text-banner-subtitle',
                        !slide.subtitleColor?.hex && 'text-white/80 md:text-muted-foreground'
                      )}
                      style={
                        slide.subtitleColor?.hex ? { color: slide.subtitleColor.hex } : undefined
                      }
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    >
                      {slide.subtitle}
                    </motion.p>
                  )}
                </div>
                {slide.ctas && slide.ctas.length > 0 && (
                  <motion.div
                    className="flex gap-4 justify-start flex-wrap"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  >
                    {slide.ctas.map((cta) => (
                      <CTAButton
                        key={cta._id}
                        cta={cta}
                        size="default"
                        className="md:h-12 md:px-8 md:text-base"
                      />
                    ))}
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  // Multiple slides - use carousel with parallax
  const firstSlide = section.slides?.[0];
  const bgColor = firstSlide?.backgroundColor?.hex;
  const bgImageUrl = firstSlide?.backgroundImage
    ? urlFor(firstSlide.backgroundImage).url()
    : undefined;
  const hasBackground = !!bgColor || !!bgImageUrl;

  return (
    <section
      ref={heroRef}
      className={cn(
        'banner-section relative overflow-hidden -mt-16 md:mt-0',
        pageSlug && `banner-section--${pageSlug}`
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
        <div className="relative aspect-9/16 md:aspect-video overflow-hidden">
          {bgColor ? (
            <div className="absolute inset-0" style={{ backgroundColor: bgColor }} />
          ) : hasBackground && firstSlide?.backgroundImage ? (
            <>
              {/* Mobile image */}
              <Image
                src={urlFor(firstSlide.mobileBackgroundImage || firstSlide.backgroundImage)
                  .width(1080)
                  .quality(85)
                  .url()}
                alt=""
                fill
                priority
                className="object-cover md:hidden"
              />
              {/* Desktop image */}
              <Image
                src={urlFor(firstSlide.backgroundImage).width(1920).quality(85).url()}
                alt=""
                fill
                priority
                className="hidden md:block object-cover object-center"
              />
            </>
          ) : (
            <Image src={FALLBACK_BANNER_IMAGE} alt="" fill priority className="object-cover" />
          )}
          <motion.div
            style={{ y: contentY, opacity: contentOpacity }}
            className="relative z-20 h-full flex items-end md:items-center"
          >
            <Carousel className="w-full md:max-w-2xl lg:max-w-[50%]">
              <CarouselContent>
                {section.slides?.map((slide, index) => (
                  <CarouselItem
                    key={index}
                    style={{ display: index === current ? 'block' : 'none' }}
                  >
                    <motion.div
                      className="banner-slide text-left px-6 py-6 md:py-8 md:px-16 bg-primary/90 backdrop-blur-md text-white md:bg-transparent md:backdrop-blur-none md:text-inherit"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <div className="mb-4 md:mb-6">
                        <motion.h1
                          className="font-normal mb-4 text-banner-title leading-banner-title"
                          style={
                            slide.titleColor?.hex ? { color: slide.titleColor.hex } : undefined
                          }
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.8, delay: 0.3 }}
                        >
                          <PortableText value={slide.title} components={bannerTitleComponents} />
                        </motion.h1>
                        {slide.subtitle && (
                          <motion.p
                            className={cn(
                              'text-banner-subtitle',
                              !slide.subtitleColor?.hex && 'text-white/80 md:text-muted-foreground'
                            )}
                            style={
                              slide.subtitleColor?.hex
                                ? { color: slide.subtitleColor.hex }
                                : undefined
                            }
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                          >
                            {slide.subtitle}
                          </motion.p>
                        )}
                      </div>
                      {slide.ctas && slide.ctas.length > 0 && (
                        <motion.div
                          className="flex gap-4 justify-start flex-wrap"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.8, delay: 0.5 }}
                        >
                          {slide.ctas.map((cta) => (
                            <CTAButton
                              key={cta._id}
                              cta={cta}
                              size="default"
                              className="md:h-12 md:px-8 md:text-base"
                            />
                          ))}
                        </motion.div>
                      )}
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
